import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_DOCUMENT_TEMPLATE_DEFAULT } from '../../../app/constants/paths.constant';
import type {
  ReferencesContext_DocumentType,
  ReferencesContext_GenericStatus,
} from '../../../contexts/ReferencesContext';

import { STATUS_ID } from '../constants/data-grid.constant';
import type { DocumentTemplatesPage_List_DocumentTemplate } from '../hooks/useFindAllDocumentTemplatesQuery.hook';

interface Props {
  readonly documentTemplate: DocumentTemplatesPage_List_DocumentTemplate;
}

const Action = ({ documentTemplate }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_DOCUMENT_TEMPLATE_DEFAULT, {
      id: documentTemplate._id,
    });
    void navigate(path);
  }, [navigate, documentTemplate._id]);

  const actionLabel = 'Consulter le Modèle de Document';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  documentTypes: readonly ReferencesContext_DocumentType[],
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<DocumentTemplatesPage_List_DocumentTemplate>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (
      params: Readonly<
        GridRowParams<DocumentTemplatesPage_List_DocumentTemplate>
      >,
    ) => [<Action documentTemplate={params.row} key="consult" />],
  },
  {
    field: 'category',
    headerName: 'Catégorie',
    width: 200,
    description: 'Catégorie',
    editable: false,
    sortable: true,
    type: 'singleSelect',
    valueGetter: (
      _: unknown,
      row: DocumentTemplatesPage_List_DocumentTemplate,
    ) =>
      documentTypes.find(
        (documentType) => documentType._id === row.document.typeId,
      )?.code ?? 'Inconnu',
    valueOptions: documentTypes.map((documentType) => ({
      value: documentType.code,
      label: documentType.label,
    })),
  },
  {
    field: nameof<DocumentTemplatesPage_List_DocumentTemplate>('code'),
    headerName: 'Code',
    width: 350,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<DocumentTemplatesPage_List_DocumentTemplate>('label'),
    headerName: 'Libellé',
    width: 400,
    description: 'Libellé',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: 'title',
    headerName: 'Titre',
    width: 600,
    description: 'Titre',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: DocumentTemplatesPage_List_DocumentTemplate,
    ) => row.document.title,
  },
  {
    field: STATUS_ID,
    headerName: 'Statut',
    width: 200,
    description: 'Statut',
    editable: false,
    sortable: true,
    hideable: true,
    type: 'singleSelect',
    valueGetter: (
      _: unknown,
      row: DocumentTemplatesPage_List_DocumentTemplate,
    ) =>
      genericStatuses.find(
        (genericStatus) => genericStatus._id === row.statusId,
      )?.code ?? 'Inconnu',
    valueOptions: genericStatuses.map((genericStatus) => ({
      value: genericStatus.code,
      label: genericStatus.label,
    })),
  },
];
